/**
 * Page-level wrapper for the regional map filter. Composes the domain hooks from
 * region-filter-section/ and presents the UI shell (titles, buttons, map container,
 * badges). Stays free of business logic: only wires hook outputs into controls and layout.
 *
 * Behavior overview:
 * - Renders selectable layers (federal states, administrative districts, districts, postal codes)
 *   with selections managed by the reducer exposed via useRegionalFilterState.
 * - Shows layer toggle buttons based on availability from the selection model and keeps the reducer
 *   layer in sync when a button is clicked.
 * - Displays pan mode toggle (or Shift key) to allow zoom gestures, delegated to usePanMode.
 * - Displays badges for the current selection set, including enriched PLZ labels for postal codes.
 * - Notifies parent via onSelectionChange whenever the selection set changes.
 *
 * Data flow:
 * - useRegionalFilterState composes useSelectionModel (selection reducer), useViewport
 *   (projection + sizing), and usePanMode (zoom gating).
 * - Reducer state (activeState, detailLayer, regions set) flows into MapView props and badges;
 *   geography and projection also come from the composed hook.
 * - MapView raises selection events (click/touch) into handleSelection, which dispatches reducer
 *   events; hover state is local to this wrapper for lightweight UI feedback.
 * - onSelectionChange lets the parent react (e.g., reset pagination) without owning the reducer.
 *
 * Interaction model:
 * - Click/tap a state or region to toggle selection; reducer handles normalization, pruning,
 *   and invariant enforcement (state single-select; deeper layers multi-select).
 * - Detail layer buttons request layer changes; reducer clamps to available layers and prunes
 *   deeper selections when necessary.
 * - Pan mode can be toggled via button or Shift; zoom gestures are allowed only when pan is active.
 * - Hover updates a local piece of state to drive visual feedback in MapView.
 *
 * Author: bb
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card, CardContent } from "../ui/card";
import { MapPin, ChevronsDown, Move, Search } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import {
  buildRegionSelectionKey,
  getFeatureDisplayName,
  getFeatureParentState,
  getPostalCodeDisplayLabel,
  MapLayerId,
  parseRegionSelectionKey,
} from "./region-filter-section/mapHelpers";
import { DETAIL_LAYER_LABELS } from "./region-filter-section/map-constants";
import { MapView } from "./region-filter-section/MapView";
import type { RegionalFilterSectionProps } from "./region-filter-section/RegionalFilter.types";
import { useRegionalFilterState } from "./region-filter-section/useRegionalFilterState";
import { BUNDESLAENDER } from "@/services/mockData";
import { getPublicRegionCatalog } from "@/services/region.service";
import { formatRegionLabel } from "@/utils/regionLabel";

type RegionOption = {
  id: string;
  label: string;
  subtitle: string;
  parentState: string;
  layerId: MapLayerId;
  regionSelectionKey?: string;
  searchText: string;
  labelNormalized: string;
};

const normalizeText = (value: string | null | undefined): string =>
  (value ?? "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

/**
 * Map-based regional selector for Germany.
 * Renders selectable layers (states/districts) and delegates selection changes upward.
 */
export const RegionalFilterSection: React.FC<RegionalFilterSectionProps> = ({
  handleViewResults,
  selectedRegionKeys,
  onSelectionChange,
  onMapStateChange,
  regionCounts,
  language = "de",
  t,
}) => {
  const {
    selection,
    activeState,
    detailLayer,
    availableDetailLayers,
    panModeEnabled,
    setPanModeEnabled,
    panModeActive,
    width,
    height,
    hasAllSelected,
    selectedStateSet,
    regionSelectionsByState,
    geography,
    allLayers,
    projectionCenter,
    projectionScale,
    zoomGroupKey,
    handleZoomFilter,
    handleSelection,
    setDetailLayer,
    resetSelection,
  } = useRegionalFilterState();
  const [hoveredState, setHoveredState] = useState<string | null>(null);
  const [regionQuery, setRegionQuery] = useState("");
  const [catalogLabels, setCatalogLabels] = useState<Record<string, string>>({});
  const [hoverLabelByRegionKey, setHoverLabelByRegionKey] = useState<
    Record<string, string>
  >({});
  const selectionList = useMemo(
    () => Array.from(selection.regions),
    [selection.regions]
  );
  const externalSelectionList = useMemo(
    () =>
      Array.from(
        new Set(
          (selectedRegionKeys ?? [])
            .map((entry) => entry?.trim())
            .filter((entry): entry is string => !!entry)
        )
      ).sort((left, right) => left.localeCompare(right)),
    [selectedRegionKeys]
  );
  const externalSelectionSignature = useMemo(
    () => externalSelectionList.join("|"),
    [externalSelectionList]
  );
  const selectionSignature = useMemo(
    () =>
      selectionList
        .slice()
        .sort((left, right) => left.localeCompare(right))
        .join("|"),
    [selectionList]
  );
  const pendingExternalSyncSignatureRef = useRef<string | null>(null);
  const previousExternalSelectionSignatureRef = useRef<string | null>(null);
  const titleTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 77%, var(--surface))",
  } as const;
  const bodyTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 66%, var(--surface))",
  } as const;
  const iconStyle = {
    color:
      "color-mix(in srgb, color-mix(in srgb, var(--primary) 70%, var(--accent)) 90%, var(--foreground))",
  } as const;
  const actionButtonStyle = {
    "--action-bg": "color-mix(in srgb, var(--accent) 14%, transparent)",
    "--action-hover": "color-mix(in srgb, var(--accent) 30%, transparent)",
    "--action-border": "color-mix(in srgb, var(--accent) 35%, var(--foreground))",
  } as React.CSSProperties;
  const activeLayerStyle = {
    "--layer-active-bg":
      "color-mix(in srgb, var(--accent) 70%, var(--foreground))",
    "--layer-active-bg-hover":
      "color-mix(in srgb, var(--accent) 60%, var(--foreground))",
    "--layer-active-border":
      "color-mix(in srgb, var(--accent) 78%, var(--foreground))",
    "--layer-active-text": "var(--background)",
    backgroundColor: "var(--layer-active-bg)",
    borderColor: "var(--layer-active-border)",
    color: "var(--layer-active-text)",
  } as React.CSSProperties;

  useEffect(() => {
    if (
      pendingExternalSyncSignatureRef.current === externalSelectionSignature &&
      externalSelectionSignature === selectionSignature
    ) {
      pendingExternalSyncSignatureRef.current = null;
    }
  }, [
    externalSelectionSignature,
    selectionSignature,
  ]);

  useEffect(() => {
    const previousExternalSelectionSignature =
      previousExternalSelectionSignatureRef.current;
    const isInitialMount = previousExternalSelectionSignature === null;
    if (
      !isInitialMount &&
      previousExternalSelectionSignature === externalSelectionSignature
    ) {
      return;
    }

    previousExternalSelectionSignatureRef.current = externalSelectionSignature;
    if (externalSelectionSignature === selectionSignature) {
      if (
        pendingExternalSyncSignatureRef.current === externalSelectionSignature
      ) {
        pendingExternalSyncSignatureRef.current = null;
      }
      return;
    }

    pendingExternalSyncSignatureRef.current = externalSelectionSignature;
    resetSelection();
    externalSelectionList.forEach((entry) => {
      const parsed = parseRegionSelectionKey(entry);
      if (!parsed) return;
      handleSelection(parsed.state, entry, parsed.layerId);
    });
  }, [
    externalSelectionList,
    externalSelectionSignature,
    handleSelection,
    resetSelection,
    selectionSignature,
  ]);

  useEffect(() => {
    if (!onSelectionChange) return;
    if (pendingExternalSyncSignatureRef.current !== null) return;
    onSelectionChange(selectionList);
  }, [onSelectionChange, selectionList]);

  useEffect(() => {
    if (!onMapStateChange) return;
    onMapStateChange(detailLayer, activeState);
  }, [activeState, detailLayer, onMapStateChange]);

  useEffect(() => {
    let cancelled = false;
    getPublicRegionCatalog({ includePostal: true, language })
      .then((rows) => {
        if (cancelled) return;
        const labels: Record<string, string> = {};
        const hoverLabels: Record<string, string> = {};
        rows.forEach((row) => {
          if (row.regionKey) {
            labels[row.regionKey] = row.displayLabel;
            hoverLabels[row.regionKey] =
              row.layerId === "federalStates"
                ? formatRegionLabel(row.stateName, row.stateNameEn ?? null, language)
                : formatRegionLabel(row.regionName, row.regionNameEn ?? null, language);
          }
        });
        setCatalogLabels(labels);
        setHoverLabelByRegionKey(hoverLabels);
      })
      .catch(() => {
        if (cancelled) return;
        setCatalogLabels({});
        setHoverLabelByRegionKey({});
      });

    return () => {
      cancelled = true;
    };
  }, [language]);

  // Build the layer toggle buttons based on what the hook says is available.
  const detailOptions = useMemo(
    () =>
      availableDetailLayers.map((id) => ({
        id,
        label: t(DETAIL_LAYER_LABELS[id]),
      })),
    [availableDetailLayers, t]
  );

  const layerLabels = useMemo(
    () => ({
      federalStates: t(DETAIL_LAYER_LABELS.federalStates),
      administrativeDistricts: t(DETAIL_LAYER_LABELS.administrativeDistricts),
      districts: t(DETAIL_LAYER_LABELS.districts),
      postalCodes: t(DETAIL_LAYER_LABELS.postalCodes),
    }),
    [t]
  );

  const layerButtonClass = "h-8";
  const activeLayerClass =
    "hover:[--layer-active-bg:var(--layer-active-bg-hover)] hover:text-[var(--layer-active-text)]";

  const regionOptions = useMemo(() => {
    const options: RegionOption[] = [];

    const addOption = (layerId: MapLayerId, feature: any) => {
      const featureName = getFeatureDisplayName(layerId, feature);
      const parentState =
        layerId === "federalStates"
          ? featureName
          : getFeatureParentState(layerId, feature);

      const hasValidState =
        !!parentState &&
        BUNDESLAENDER.includes(parentState as (typeof BUNDESLAENDER)[number]);
      if (!featureName || !hasValidState) return;

      const regionSelectionKey =
        layerId === "federalStates"
          ? undefined
          : buildRegionSelectionKey(layerId, feature, parentState);

      const labelText = featureName;
      const subtitle =
        layerId === "federalStates"
          ? layerLabels[layerId]
          : `${parentState} • ${layerLabels[layerId]}`;
      const normalized = normalizeText(
        `${labelText} ${parentState ?? ""} ${layerLabels[layerId]}`
      );

      options.push({
        id:
          regionSelectionKey ??
          buildRegionSelectionKey("federalStates", parentState, parentState),
        label: labelText,
        subtitle,
        parentState,
        layerId,
        regionSelectionKey,
        searchText: normalized,
        labelNormalized: normalizeText(labelText),
      });
    };

    const layerEntries: Array<[MapLayerId, any]> = [
      ["federalStates", allLayers.federalStates],
      ["administrativeDistricts", allLayers.administrativeDistricts],
      ["districts", allLayers.districts],
      ["postalCodes", allLayers.postalCodes],
    ];

    layerEntries.forEach(([layerId, geo]) => {
      const features = geo?.features;
      if (!Array.isArray(features)) return;
      features.forEach((feature) => addOption(layerId, feature));
    });

    return options;
  }, [
    allLayers.administrativeDistricts,
    allLayers.districts,
    allLayers.federalStates,
    allLayers.postalCodes,
    layerLabels,
  ]);

  const regionSearchResults = useMemo(() => {
    const term = normalizeText(regionQuery.trim());
    if (!term) return [];
    const matches = regionOptions
      .map((option) => {
        const hitIndex = option.searchText.indexOf(term);
        if (hitIndex === -1) return null;
        const layerMatch = option.layerId === detailLayer ? 3 : 0;
        const stateMatch =
          normalizeText(activeState) === normalizeText(option.parentState)
            ? 2
            : 0;
        const startsWith = option.labelNormalized.startsWith(term) ? 2 : 0;
        const positionScore = hitIndex === 0 ? 1 : 0.5;
        const score = layerMatch + stateMatch + startsWith + positionScore;
        return { option, score };
      })
      .filter(Boolean) as Array<{ option: RegionOption; score: number }>;

    matches.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.option.label.localeCompare(b.option.label);
    });

    return matches.slice(0, 8).map((entry) => entry.option);
  }, [activeState, detailLayer, regionOptions, regionQuery]);

  const handleRegionSearchSelect = (option: RegionOption) => {
    setRegionQuery("");
    handleSelection(
      option.parentState,
      option.layerId === "federalStates" ? undefined : option.regionSelectionKey,
      option.layerId
    );
    setHoveredState(
      option.layerId === "federalStates"
        ? buildRegionSelectionKey(
            "federalStates",
            option.parentState,
            option.parentState
          )
        : option.regionSelectionKey ?? null
    );
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-1 min-w-0 items-start gap-3">
          <div className="icon-box">
            <MapPin className="w-5 h-5 icon-primary" style={iconStyle} />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-lg font-semibold" style={titleTextStyle}>
                {t("categories.regionalFilterHeading")}
              </h3>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={t("categories.germanyMapFilterDescription")}
                    className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600 dark:focus-visible:ring-offset-slate-900"
                    style={iconStyle}
                  >
                    ?
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  align="center"
                  sideOffset={6}
                  style={{
                    whiteSpace: "normal",
                    maxWidth: "min(90vw, 40rem)",
                  }}
                  className="text-left leading-relaxed break-words"
                >
                  {t("categories.germanyMapFilterDescription")}
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleViewResults}
          className="gap-2 px-3 py-1.5 rounded-full border border-[var(--action-border)] bg-[var(--action-bg)] hover:bg-[var(--action-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
          style={{ ...actionButtonStyle, ...titleTextStyle }}
        >
          <ChevronsDown className="w-4 h-4" style={iconStyle} />
          {t("categories.showResults")}
        </Button>
      </div>
      <div className="grid grid-cols-1 gap-6">
        <Card className="stat-card">
        <CardContent className="space-y-4 pt-6">
            {/* Region search across all layers */}
            <div>
              <div className="relative">
                <Search
                  className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-secondary"
                  style={iconStyle}
                />
                <Input
                  value={regionQuery}
                  onChange={(e) => setRegionQuery(e.target.value)}
                  placeholder={t("categories.regionSearchPlaceholder")}
                  className="pl-9 text-sm"
                />
              </div>
            {regionQuery.trim() ? (
                <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm max-h-64 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
                  {regionSearchResults.length === 0 ? (
                    <div
                      className="px-3 py-2 text-xs text-secondary"
                      style={bodyTextStyle}
                    >
                      {t("categories.regionSearchNoResults")}
                    </div>
                  ) : (
                    regionSearchResults.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => handleRegionSearchSelect(option)}
                        className="w-full text-left px-3 py-2 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 flex flex-col gap-0.5"
                      >
                        <span
                          className="text-sm font-semibold"
                          style={titleTextStyle}
                        >
                          {option.label}
                        </span>
                        <span className="text-xs text-secondary" style={bodyTextStyle}>
                          {option.subtitle}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              ) : null}
            </div>
            <div>
              <button
                type="button"
                role="switch"
                aria-checked={panModeEnabled}
                onClick={() => setPanModeEnabled((prev) => !prev)}
                className={`w-full text-left flex flex-col gap-1 rounded-lg border px-4 py-3 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                  panModeActive
                    ? "border-blue-200 bg-blue-50/80 text-blue-800 dark:border-blue-900/50 dark:bg-blue-950/30 dark:text-blue-100"
                    : "border-slate-200 bg-white/80 text-slate-800 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-100"
                }`}
              >
                <div className="flex flex-wrap items-center gap-3">
                  <div
                    className={`rounded-full p-2 transition-colors ${
                      panModeActive
                        ? "bg-blue-600/10 text-blue-600 dark:text-blue-300"
                        : "bg-slate-200/80 text-slate-500 dark:bg-slate-800/60 dark:text-slate-300"
                    }`}
                  >
                    <Move className="w-4 h-4" style={iconStyle} />
                  </div>
                  <div className="min-w-[200px] flex-1">
                    <p className="text-sm font-medium" style={titleTextStyle}>
                      {t("categories.panModeLabel")}
                    </p>
                    <p
                      className={`text-xs ${
                        panModeActive
                          ? "text-blue-700 dark:text-blue-200"
                          : "text-secondary"
                      }`}
                      style={panModeActive ? undefined : bodyTextStyle}
                    >
                      {panModeActive
                        ? t("categories.panModeActive")
                        : t("categories.panModeHint")}
                    </p>
                  </div>
                  <span
                    className={`text-xs font-medium px-2 py-1 rounded-full border transition-colors ${
                      panModeActive
                        ? "border-blue-300 text-blue-700 dark:border-blue-800 dark:text-blue-100"
                        : "border-slate-200 text-secondary dark:border-slate-600"
                    }`}
                    style={panModeActive ? undefined : bodyTextStyle}
                  >
                    {t("categories.panModeShiftKey")}
                  </span>
                </div>
              </button>
            </div>
            {activeState && (
              <div className="flex flex-col gap-1.5">
                <div
                  className="flex items-center gap-2 text-sm font-medium"
                  style={titleTextStyle}
                >
                  {t("categories.mapDetailSectionTitle")}
                  <span
                    className="text-xs font-normal text-secondary"
                    style={bodyTextStyle}
                  >
                    {t("categories.mapLayerLabel")}
                  </span>
                </div>
                {/* Detail layer toggle buttons, now including postal codes */}
                <div className="flex flex-wrap items-center gap-2 justify-between">
                  <div className="flex flex-wrap gap-2">
                    {detailOptions.map((option) => {
                      const isActive = option.id === detailLayer;
                      return (
                        <Button
                          key={option.id}
                          variant="outline"
                          size="sm"
                          onClick={() => setDetailLayer(option.id)}
                          className={
                            isActive
                              ? `${layerButtonClass} ${activeLayerClass}`
                              : layerButtonClass
                          }
                          style={isActive ? activeLayerStyle : titleTextStyle}
                        >
                          {option.label}
                        </Button>
                      );
                    })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={resetSelection}
                    className="h-8"
                    style={titleTextStyle}
                  >
                    {t("categories.resetSelection")}
                  </Button>
                </div>
              </div>
            )}
            {/* Map container with responsive gradient background */}
            <div
              className="mx-[-1rem] sm:mx-0 rounded-none sm:rounded-xl p-0 sm:p-6
                bg-gradient-to-br from-blue-50/50 to-gray-50/50
                dark:from-blue-900/10 dark:to-slate-800
                min-h-[420px] sm:min-h-[580px] md:min-h-[650px]"
            >
              <div
                className="mx-[-1rem] sm:mx-0 rounded-none sm:rounded-xl p-0 sm:p-6
                  bg-gradient-to-br from-blue-50/50 to-gray-50/50
                  dark:from-blue-900/10 dark:to-slate-800
                  min-h-[520px] sm:min-h-[600px] md:min-h-[650px]"
              >
                <div className="mx-auto w-full max-w-[1600px]">
                  <MapView
                    width={width}
                    height={height}
                    projectionCenter={projectionCenter}
                    projectionScale={projectionScale}
                    zoomGroupKey={zoomGroupKey}
                    handleZoomFilter={handleZoomFilter}
                    geography={geography}
                    allLayers={allLayers}
                    detailLayer={detailLayer}
                    activeState={activeState}
                    hoveredState={hoveredState}
                    setHoveredState={setHoveredState}
                    handleSelection={handleSelection}
                    regionSelectionsByState={regionSelectionsByState}
                    selectedStateSet={selectedStateSet}
                    hasAllSelected={hasAllSelected}
                    regionCounts={regionCounts}
                    hoverLabelByRegionKey={hoverLabelByRegionKey}
                  />
                </div>
              </div>

              <div className="mt-2 flex flex-col gap-2 px-4 sm:px-0">
                <div className="flex flex-wrap gap-2">
                  {hasAllSelected ? (
                    <Badge
                      variant="secondary"
                      className="bg-blue-50 text-blue-700"
                    >
                      {t("categories.allStatesSelected")}
                    </Badge>
                  ) : (
                    Array.from(selection.regions).map((entry) => {
                      const parsed = parseRegionSelectionKey(entry);
                      const catalogLabel = catalogLabels[entry];
                      const label = parsed
                        ? catalogLabel ||
                          (parsed.layerId === "federalStates"
                          ? parsed.state
                          : parsed.layerId === "postalCodes"
                          ? `${
                              getPostalCodeDisplayLabel(parsed.regionName) ??
                              parsed.regionName
                            } (${parsed.state})`
                          : `${parsed.regionName} (${parsed.state})`)
                        : entry;
                      return (
                        <Badge
                          key={entry}
                          variant="secondary"
                          className="bg-blue-50 text-blue-700"
                        >
                          {label}
                        </Badge>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};
